import InventoryActionBar from './inventoryPageComponents/InventoryActionBar';
import InventoryDocuments from './inventoryPageComponents/InventoryDocuments';
import InventoryFilter from './inventoryPageComponents/InventoryFilter';
import InventoryStats from './inventoryPageComponents/InventoryStats';

import { useDispatch, useSelector } from 'react-redux';
import {
    fetchInventory,
    addInventoryItems,
    editInventoryItem,
    deleteInventoryItems,
    fetchExpiryStats,
} from '../../redux/action/inventoryAction/InventoryAction';

import { useSearchParams } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { LuBoxes, LuPackageSearch, LuChevronDown, LuInfo, LuX } from "react-icons/lu";
import { dotFor, ExpiryBadge, groupExpiryBadge, rowExpiryLevel } from '../../utils/Constant';
import Toaster, { notify } from '../../common/Toast';
import InventoryDetailModel from './inventoryPageComponents/InventoryDetailModel';
import Pagination from '../../common/Pagination';

const DEFAULT_PAGE_SIZE = 10;

const Field = ({ label, children, className = "" }) => (
    <div className={`min-w-0 ${className}`}>
        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
        <p className="mt-0.5 truncate text-[12.5px] font-medium text-gray-700">{children}</p>
    </div>
);

const InfoButton = ({ onClick }) => (
  <button type="button"
    onClick={(e) => { e.stopPropagation(); 
    onClick();
    }}
    title="View full details"
    className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-yellow-100 text-yellow-500 transition hover:bg-yellow-200 hover:text-yellow-700"
  >
    <LuInfo size={16} />
  </button>
);

export default function Inventory() {
    const [params] = useSearchParams();
    const dispatch = useDispatch();

    const {
        itemsByName: itemsByNameObj,
        pagination,
        numDueToExpire,
        numExpired,
        listLoading,
    } = useSelector((state) => state.inventory);

    // Redux stores itemsByName as a plain object (JSON from the API);
    // everything below expects Map semantics (.values(), .get(), .keys()).
    const itemsByName = useMemo(() => new Map(Object.entries(itemsByNameObj || {})), [itemsByNameObj]);

    const [selectedRow, setSelectedRow] = useState(new Map());
    const [filterQuery, setFilterQuery] = useState({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [reload, induceReload] = useState(false);
    const [collapsedItems, setCollapsedItems] = useState(new Set());
    const [detailItem, setDetailItem] = useState(null);

    const toggleCollapsed = (item) => {
        setCollapsedItems((prev) => {
            const next = new Set(prev);
            if (next.has(item)) next.delete(item);
            else next.add(item);
            return next;
        });
    };

    // Any change to filters (including the search box, which writes into
    // filterQuery.item via InventoryFilter) or URL params invalidates the
    // current page — always jump back to page 1 for a new result set.
    useEffect(() => {
        setPage(1);
    }, [filterQuery, params]);

    // .................... Fetch list — search + filtering is entirely
    // backend-driven; no client-side re-filtering happens here. .............

    useEffect(() => {
        const paramFilterQuery = (params.get('type') || params.get('item')) ? {
            type: params.get('type') || "",
            item: params.get('item') || "",
            addDate: [null, null],
            expireDate: [null, null],
            location: "",
            project: "",
            quantity: ["", ""],
            catalog: "",
            manufacturer: "",
            supplier: ""
        } : {};

        const isParamDriven = Object.keys(paramFilterQuery).length > 0;
        const activeQuery = isParamDriven ? paramFilterQuery : filterQuery;

        dispatch(fetchInventory(activeQuery, page, pageSize)).then((res) => {
            if (!res?.success) {
                notify.error(
                    isParamDriven
                        ? "Failed to filter inventory using the selected dashboard shortcut"
                        : "Failed to fetch inventory",
                    res?.error,
                );
            }
        });
    }, [reload, params, filterQuery, page, pageSize, dispatch]);

    const highlightId = Number(params.get('highlight')) || null;
    const [highlighted, setHighlighted] = useState(null);

    useEffect(() => {
        if (!highlightId || listLoading) return;
        const el = document.getElementById(`inventory-${highlightId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlighted(highlightId);
        const timer = setTimeout(() => setHighlighted(null), 4000);
        return () => clearTimeout(timer);
    }, [highlightId, listLoading, itemsByNameObj]);

    // .................... Expiry stats ...........................

    useEffect(() => {
        dispatch(fetchExpiryStats()).then((res) => {
            if (!res?.success) {
                notify.error("Failed to fetch expiry stats", res?.error);
            }
        });
    }, [reload, dispatch]);

    // C(R)UD

    const handleAdd = (newItem) => {
        dispatch(addInventoryItems(newItem)).then((res) => {
            if (res?.success) {
                induceReload(!reload);
                notify.success("Successful Addition", "Item(s) have been successfully added to the inventory");
            } else {
                notify.error("Failed to add to inventory", res?.error);
            }
        });
    };

    const handleEdit = (updatedItem) => {
        dispatch(editInventoryItem(selectedRow.keys().next().value, updatedItem)).then((res) => {
            if (res?.success) {
                setSelectedRow(new Map());
                induceReload(!reload);
                notify.success("Successful Update", "Item has been successfully updated in the inventory");
            } else {
                notify.error("Failed to update inventory", res?.error);
            }
        });
    };

    const handleDelete = () => {
        const deleteIds = Array.from(selectedRow.keys());
        dispatch(deleteInventoryItems(deleteIds)).then((res) => {
            if (res?.success) {
                setSelectedRow(new Map());
                induceReload(!reload);
                notify.success("Successful Deletion", "Selected item(s) have been successfully deleted from the inventory");
            } else {
                notify.error("Failed to delete from inventory", res?.error);
            }
        });
    };

    const handleRowClick = (item) => {
        const updatedSelectedRow = new Map(selectedRow);
        if (updatedSelectedRow.has(item.id)) {
            updatedSelectedRow.delete(item.id);
        } else {
            const { id, ...rest } = item;
            updatedSelectedRow.set(id, rest);
        }
        setSelectedRow(updatedSelectedRow);
    };

    const handleSelectAll = () => {
        if (selectedRow.size === 0) {
            const allItems = new Map(Array.from(itemsByName.values()).flatMap(arr => arr).map(({ id, ...rest }) => [id, rest]));
            setSelectedRow(allItems);
        } else {
            setSelectedRow(new Map());
        }
    };

    const getDateStringUnderscored = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}_${month}_${day}`;
    }

    const handleExport = () => {
        const firstItem = Array.from(itemsByName.values())[0]?.[0];
        if (!firstItem) return;
        const headers = Object.keys(firstItem).join(',');

        const rows = Array.from(itemsByName.values())
            .flat()
            .map(item => {
                if (selectedRow.size === 0 || selectedRow.has(item.id)) {
                    return Object.values(item)
                        .map(value => value instanceof Date ? value.toISOString() : value)
                        .join(',');
                }
                return null;
            })
            .filter(row => row !== null);

        const csvContent = [headers, ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'inventoryData_' + getDateStringUnderscored() + '.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        notify.success(
            "Successful Export",
            "Inventory data has been successfully saved to the local machine as a CSV file (current page only).",
        );
    };

    const handleImport = (file) => {
        const requiredSchema = [
            "item", "addDate", "expireDate", "location", "project", "type", "quantity", "catalog", "manufacturer", "supplier"
        ];

        const reader = new FileReader();
        reader.onload = (event) => {
            const rows = event.target.result.split("\n").map(row => row.split(","));
            const headers = rows[0].map(header => header.trim());

            const isValidSchema = requiredSchema.every(field => headers.includes(field));
            if (!isValidSchema) {
                notify.error("Invalid CSV Schema", "The CSV file does not match the required schema.");
                return;
            }

            const items = rows.slice(1).map(row => {
                const item = {};
                headers.forEach((header, index) => {
                    const value = row[index]?.trim();
                    if (header === "numUnits" || header === "inStock") {
                        item[header] = parseInt(value, 10);
                    } else if (header === "addDate" || header === "expireDate") {
                        item[header] = new Date(value);
                    } else {
                        item[header] = value;
                    }
                });
                return item;
            });

            handleAdd(items);
            notify.success("Successful Import", "CSV file data has been successfully added to the database");
        };
        reader.readAsText(file.files[0]);
    };

    // Only still needed for InventoryActionBar's cellPhoneView prop.
    function useWindowWidth() {
        const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
        useLayoutEffect(() => {
            const update = () => setW(window.innerWidth);
            window.addEventListener('resize', update);
            update();
            return () => window.removeEventListener('resize', update);
        }, []);
        return w;
    }
    const width = useWindowWidth();
    const cellPhoneView = width < 768;

    // Dropdown option lists for the filter modal are derived from whatever
    // page of data is currently loaded — a reasonable approximation without
    // a dedicated lookups endpoint.
    const allRows = Array.from(itemsByName.values()).flat();
    const uniqueValues = (key) => [...new Set(allRows.map((r) => r[key]).filter(Boolean))];
    const filterOptions = {
        itemNames: Array.from(itemsByName.keys()),
        types: uniqueValues("type"),
        manufacturers: uniqueValues("manufacturer"),
        suppliers: uniqueValues("supplier"),
        projects: uniqueValues("project"),
        locations: uniqueValues("location"),
    };

    const { totalItems = 0, totalPages = 1 } = pagination || {};
    const hasActiveFilters = Object.keys(filterQuery).length > 0 || params.size > 0;

    const goToPage = (p) => {
        if (p < 1 || p > totalPages || p === page) return;
        setSelectedRow(new Map());
        setPage(p);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const changePageSize = (size) => {
        setSelectedRow(new Map());
        setPageSize(size);
        setPage(1);
    };

    return (
        <div className="w-full bg-gray-50 pb-24">
            <div className="mx-auto w-full max-w-[1600px] px-3 py-4 sm:px-4 lg:px-6">
                {/* ---------------------------- Header ---------------------------- */}
                <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-900 text-white">
                            <LuBoxes size={16} />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-[15px] font-semibold text-gray-900">Inventory</h1>
                            <p className="truncate text-[12px] text-gray-500">
                                Track stock, expiry, and documents by item.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                    {(params.get('type') || params.get('item')) && (
                        <a
                            href="/inventory"
                            className="flex h-8 items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2.5 text-[12px] font-medium text-amber-700 transition hover:bg-amber-100"
                        >
                            <LuX size={12} />
                            Clear URL filter
                        </a>
                    )}
                    <InventoryFilter
                        query={filterQuery}
                        onApply={setFilterQuery}
                        onClear={() => setFilterQuery({})}
                        itemNames={filterOptions.itemNames}
                        types={filterOptions.types}
                        manufacturers={filterOptions.manufacturers}
                        suppliers={filterOptions.suppliers}
                        projects={filterOptions.projects}
                        locations={filterOptions.locations}
                        disabled={totalItems === 0 && !hasActiveFilters}
                        handleAdd={handleAdd}
                    />
                    </div>
                </div>

                <InventoryStats
                    numItems={totalItems}
                    numDueToExpire={numDueToExpire}
                    numExpired={numExpired}
                    setQuery={setFilterQuery}
                />

                {listLoading && (
                    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                        <p className="text-[12.5px] text-gray-400">Loading inventory…</p>
                    </div>
                )}

                {!listLoading && totalItems === 0 && (
                    <div className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-200 bg-white py-16 text-center">
                        <LuPackageSearch size={24} className="mb-1 text-gray-300" strokeWidth={1.5} />
                        <p className="text-[12.5px] font-medium text-gray-600">No items found</p>
                        <p className="max-w-xs text-[12px] text-gray-400">
                            {hasActiveFilters
                                ? "Nothing matches the applied filters."
                                : "Items will be grouped here by name once added."}
                        </p>
                    </div>
                )}

                {/* ------------------------- Item groups ------------------------ */}
                {!listLoading && Array.from(itemsByName.keys()).map((item) => {
                    const rows = itemsByName.get(item) || [];
                    if (rows.length === 0) return null;
                    const isCollapsed = collapsedItems.has(item);
                    return (
                        <div key={item} className="mb-5 overflow-hidden rounded-lg border border-gray-200 bg-white">
                            {/* Item bar — click to expand/collapse this group */}
                            <button
                                type="button"
                                onClick={() => toggleCollapsed(item)}
                                aria-expanded={!isCollapsed}
                                className={`flex w-full flex-col gap-2 bg-white px-3.5 py-2.5 text-left transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between ${
                                    !isCollapsed ? "border-b border-gray-100" : ""
                                }`}
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotFor(item)}`} />
                                    <span className="truncate text-[13px] font-semibold text-gray-900">{item}</span>
                                    <span className="shrink-0 text-[11px] text-gray-400">
                                        {rows.length} {rows.length === 1 ? "batch" : "batches"}
                                    </span>
                                    {groupExpiryBadge(rows)}
                                </div>
                                <LuChevronDown
                                    size={16}
                                    className={`shrink-0 text-gray-400 transition-transform ${isCollapsed ? "" : "rotate-180"}`}
                                />
                            </button>

                            {/* Batches are rows inside the same card, separated by
                                dividers, not a separate box below the header */}
                            {!isCollapsed && (
                                <div className="divide-y divide-gray-100">
                                    {rows.map((row) => {
                                        const isSelected = selectedRow.has(row.id);
                                        const level = rowExpiryLevel(row);
                                        return (
                                            <div
                                                key={row.id}
                                                id={`inventory-${row.id}`}
                                                onClick={() => handleRowClick(row)}
                                                className={`w-full min-w-0 cursor-pointer border-l-[3px] p-3 transition-colors sm:p-4 ${
                                                    isSelected
                                                        ? "border-l-indigo-500 bg-indigo-50/50"
                                                        : highlighted === row.id
                                                        ? "border-l-amber-400 bg-amber-50"
                                                        : "border-l-transparent hover:bg-gray-50/60"
                                                }`}
                                            >
                                                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
                                                    {/* Identity — badge lives here, never squeezed into
                                                        the Expires field so it can't get clipped */}
                                                    <div className="flex items-start gap-2 lg:w-56 lg:shrink-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleRowClick(row)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-1 focus:ring-indigo-500/40"
                                                        />
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                                <span className="truncate text-[13px] font-semibold leading-tight text-gray-900">
                                                                    {row.catalog || row.type || "Batch"}
                                                                </span>
                                                                <InfoButton onClick={() => setDetailItem(rows)} />
                                                                <ExpiryBadge level={level} />
                                                            </div>
                                                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                                                                Added {(new Date(row.addDate)).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Data fields — 2 columns up to tablet width, only
                                                        goes to a single 6-column row once there's genuinely
                                                        enough space (lg / 1024px+) */}
                                                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-gray-100 pt-2.5 sm:grid-cols-3 lg:flex-1 lg:grid-cols-6 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
                                                        <Field label="Expires">{(new Date(row.expireDate)).toLocaleDateString()}</Field>
                                                        <Field label="Qty" className="font-mono">{row.quantity}</Field>
                                                        <Field label="Location">{row.location}</Field>
                                                        <Field label="Type">{row.type}</Field>
                                                        <Field label="Manufacturer">{row.manufacturer}</Field>
                                                        <Field label="Supplier">{row.supplier}</Field>
                                                    </div>

                                                    {/* Project (optional) */}
                                                    {row.project && (
                                                        <div className="border-t border-gray-100 pt-2.5 lg:w-36 lg:shrink-0 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
                                                            <Field label="Project">{row.project}</Field>
                                                        </div>
                                                    )}

                                                    {/* Documents */}
                                                    <div
                                                        className="flex items-center gap-2 border-t border-gray-100 pt-2.5 lg:shrink-0 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <InventoryDocuments
                                                            id={row.id}
                                                            poDoc={row.poDoc}
                                                            quotationDoc={row.quotationDoc}
                                                            msdsDoc={row.msdsDoc}
                                                            coaDoc={row.coaDoc}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* ------------------------- Pagination ------------------------ */}
                {!listLoading && (
                    <Pagination
                        page={pagination?.page || page}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        pageSize={pageSize}
                        onPageChange={goToPage}
                        onPageSizeChange={changePageSize}
                        pageSizeOptions={[10, 20, 50]}
                        itemLabel={totalItems === 1 ? "item" : "items"}
                        loading={listLoading}
                    />
                )}
            </div>

            <Toaster />
            <InventoryActionBar
                selectedRow={selectedRow}
                handleAdd={handleAdd}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                handleSelectAll={handleSelectAll}
                handleExport={handleExport}
                handleImport={handleImport}
                cellPhoneView={cellPhoneView}
            />
            
            <InventoryDetailModel item={detailItem} onClose={() => setDetailItem(null)} /> 
        </div>
    );
}